import { formatNumber } from './bodyMeasurementsMath';

/**
 * Faixas de referência dos indicadores, para a régua dos cartões.
 *
 * É a referência usual que o profissional já conhece (IMC pela OMS, gordura
 * corporal pelas faixas do ACE por sexo, peso sugerido pela altura via IMC),
 * mostrada como apoio visual. Não é diagnóstico e não bloqueia nada.
 *
 * Cada faixa: { label, to (limite superior, exclusivo; null = sem teto), tone }.
 */

export const RULER_TONE = Object.freeze({
  OK: 'ok',
  WARN: 'warn',
  ALERT: 'alert',
  NEUTRAL: 'neutral',
});

// Classificação do IMC em adultos e risco de comorbidades: OMS, Obesity:
// preventing and managing the global epidemic (WHO Technical Report Series 894, 2000)
const BMI_BANDS = [
  { label: 'Baixo peso', to: 18.5, tone: RULER_TONE.WARN, risk: 'Risco aumentado de outros problemas clínicos' },
  { label: 'Eutrofia', to: 25, tone: RULER_TONE.OK, risk: 'Risco médio' },
  { label: 'Sobrepeso', to: 30, tone: RULER_TONE.WARN, risk: 'Risco aumentado' },
  { label: 'Obesidade grau I', to: 35, tone: RULER_TONE.ALERT, risk: 'Risco moderado' },
  { label: 'Obesidade grau II', to: 40, tone: RULER_TONE.ALERT, risk: 'Risco grave' },
  { label: 'Obesidade grau III', to: null, tone: RULER_TONE.ALERT, risk: 'Risco muito grave' },
];
const BMI_SCALE = { min: 12, max: 45 };

/** Idade a partir da qual vale a classificação de adultos da OMS. */
export const BMI_ADULT_AGE = 20;

/** Faixa da OMS onde o IMC caiu: { label, risk, tone }. */
export function bmiClass(bmi) {
  if (bmi === null || bmi === undefined) return null;
  return BMI_BANDS.find((band) => band.to === null || bmi < band.to);
}

// Pontos de corte da relação cintura-quadril: OMS, Waist circumference and
// waist-hip ratio: report of a WHO expert consultation (2008).
const WAIST_HIP_CUTOFF = { male: 0.9, female: 0.85 };
const WAIST_HIP_SCALE = { min: 0.6, max: 1.2 };

// Relação cintura-estatura: Ashwell M, Gibson S. BMJ Open 2016;6:e010159 (corte 0,5)
const WAIST_HEIGHT_CUTOFF = 0.5;
const WAIST_HEIGHT_SCALE = { min: 0.3, max: 0.8 };

const cutoffBands = (cutoff) => [
  { label: 'Abaixo do ponto de corte', to: cutoff, tone: RULER_TONE.OK },
  { label: 'Risco aumentado', to: null, tone: RULER_TONE.ALERT },
];

const BODY_FAT_BANDS = {
  female: [
    { label: 'Baixo', to: 14, tone: RULER_TONE.WARN },
    { label: 'Atlético', to: 21, tone: RULER_TONE.OK },
    { label: 'Em forma', to: 25, tone: RULER_TONE.OK },
    { label: 'Médio', to: 32, tone: RULER_TONE.NEUTRAL },
    { label: 'Alto', to: null, tone: RULER_TONE.WARN },
  ],
  male: [
    { label: 'Baixo', to: 6, tone: RULER_TONE.WARN },
    { label: 'Atlético', to: 14, tone: RULER_TONE.OK },
    { label: 'Em forma', to: 18, tone: RULER_TONE.OK },
    { label: 'Médio', to: 25, tone: RULER_TONE.NEUTRAL },
    { label: 'Alto', to: null, tone: RULER_TONE.WARN },
  ],
};
const BODY_FAT_SCALE = { min: 0, max: 50 };

function position(value, scale) {
  const ratio = (value - scale.min) / (scale.max - scale.min);
  return Math.min(98, Math.max(2, ratio * 100));
}

function describe(band, from, decimals) {
  const low = from === null ? null : formatNumber(from, decimals);
  const high = band.to === null ? null : formatNumber(band.to, decimals);
  if (low === null) return `${band.label} (até ${high})`;
  if (high === null) return `${band.label} (a partir de ${low})`;
  return `${band.label} (${low} a ${high})`;
}

/**
 * Monta a régua: faixas com largura em %, posição do marcador, marcas nos
 * limites e a legenda da faixa onde o valor caiu.
 */
function buildRuler(value, bands, scale, decimals, source, unit = '') {
  let from = scale.min;
  let currentIndex = bands.length - 1;
  const segments = bands.map((band, index) => {
    const to = band.to === null ? scale.max : band.to;
    const segment = { ...band, from: index === 0 ? null : from, width: ((to - from) / (scale.max - scale.min)) * 100 };
    if (band.to !== null && value < band.to && currentIndex === bands.length - 1) currentIndex = index;
    from = to;
    return segment;
  });
  const current = segments[currentIndex];
  const ticks = segments
    .filter((segment) => segment.to !== null)
    .map((segment) => ({ value: segment.to, text: formatNumber(segment.to, decimals), position: position(segment.to, scale) }));

  return {
    segments,
    ticks,
    position: position(value, scale),
    caption: describe(current, current.from, decimals) + (unit ? ` ${unit}` : ''),
    source,
  };
}

export function bmiRuler(bmi) {
  if (bmi === null || bmi === undefined) return null;
  return buildRuler(bmi, BMI_BANDS, BMI_SCALE, 1, 'OMS');
}

/** Relação cintura-quadril com o corte da OMS por sexo. Sem sexo, sem régua. */
export function waistHipRuler(ratio, variant) {
  const cutoff = WAIST_HIP_CUTOFF[variant];
  if (!cutoff || ratio === null || ratio === undefined) return null;
  return buildRuler(ratio, cutoffBands(cutoff), WAIST_HIP_SCALE, 2, 'OMS');
}

/** Relação cintura-estatura: risco aumentado a partir de 0,5, nos dois sexos. */
export function waistHeightRuler(ratio) {
  if (ratio === null || ratio === undefined) return null;
  return buildRuler(ratio, cutoffBands(WAIST_HEIGHT_CUTOFF), WAIST_HEIGHT_SCALE, 2, 'cintura/estatura');
}

/** `variant`: 'male' | 'female' | 'neutral'. Sem sexo informado não há faixa. */
export function bodyFatRuler(bodyFat, variant) {
  const bands = BODY_FAT_BANDS[variant];
  if (!bands || bodyFat === null || bodyFat === undefined) return null;
  return buildRuler(bodyFat, bands, BODY_FAT_SCALE, 0, 'ACE', '%');
}

/** Peso sugerido pela altura: a faixa de IMC adequado convertida em kg. */
export function weightRuler(weight, heightCm) {
  if (weight === null || weight === undefined || !heightCm || heightCm <= 0) return null;
  const m2 = (heightCm / 100) * (heightCm / 100);
  const bands = [
    { label: 'Abaixo', to: Math.round(18.5 * m2 * 10) / 10, tone: RULER_TONE.WARN },
    { label: 'Sugerido', to: Math.round(25 * m2 * 10) / 10, tone: RULER_TONE.OK },
    { label: 'Acima', to: null, tone: RULER_TONE.WARN },
  ];
  const scale = { min: BMI_SCALE.min * m2, max: BMI_SCALE.max * m2 };
  return buildRuler(weight, bands, scale, 1, 'pela altura', 'kg');
}
